import type { QuoteRow } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { QuoteStatusBadge } from '@/entities/quote';
import { myQuoteDetailPath } from '@/shared/config';
import { formatCount, formatDate, formatKrw } from '@/shared/lib';
import { DesignThumbnail } from '@/shared/ui';

interface RecentQuotesProps {
  readonly rows: readonly QuoteRow[];
}

/** 최근 견적 몇 건 — 견적 썸네일, 번호·상품·수량·요청일과 상태·금액. 줄 전체가 상세 링크다. */
export const RecentQuotes = ({ rows }: RecentQuotesProps): ReactElement => {
  if (rows.length === 0) {
    return <p className="py-6 text-sm text-muted-foreground">아직 요청한 견적이 없어요.</p>;
  }

  return (
    <ul className="divide-y divide-foreground/10">
      {rows.map((row) => (
        <li key={row.id}>
          <Link
            to={myQuoteDetailPath(row.id)}
            className="flex items-center gap-4 py-5 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset sm:px-2"
          >
            {/* 링크 이름은 아래 글자로 충분하다 — 그림은 꾸밈으로 둬 견적 번호를 두 번 읽지 않게 한다. */}
            <DesignThumbnail
              url={row.thumbnailUrl ?? row.productImageUrl}
              alt=""
              className="size-14 shrink-0"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-sm font-semibold tabular-nums">{row.quoteNumber}</span>
              <span className="truncate text-sm">{row.productName}</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatCount(row.quantity)}개 · {formatDate(row.createdAt)}
              </span>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <QuoteStatusBadge status={row.status} orderDisplayStatus={row.orderDisplayStatus} />
              <span className="text-sm tabular-nums">
                {row.latestIssuedTotalAmount === null ? (
                  <span className="text-xs text-muted-foreground">발행 전</span>
                ) : (
                  <span className="font-bold">{formatKrw(row.latestIssuedTotalAmount)}</span>
                )}
              </span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
};
